import { Fragment } from 'react';
import { OtherTextInput } from '@citizen-walk/shared-ui';
import {
  findOtherOption,
  getDefaultDisplayStyle,
  isOtherChosen,
  normalizeFieldOptions,
  otherTextKey,
  resolveFieldDisplayStyle,
  resolveFieldMaxLength,
  type FieldDef,
  type FieldOption,
} from '@citizen-walk/shared-types';

/**
 * 写真まとめて一括投稿ツールの「個別詳細画面」用 — 地区別 postFields 駆動の入力フォーム
 * (issue #23、Phase C)。管理者が各写真に対して、一般ユーザーと同じ地区独自の追加項目
 * (extraFields) を入力できるようにする。
 *
 * apps/user 側の `PostExtraFieldsForm` は eslint boundaries により apps 間 import 不可のため、
 * `@citizen-walk/shared-types` の共有ヘルパー (FieldDef / resolveFieldDisplayStyle /
 * getDefaultDisplayStyle / resolveFieldMaxLength / normalizeFieldOptions) を使って admin 版を
 * 最小再構築する。`getRenderablePostFields` / `EXCLUDED_POST_FIELD_KEYS` は apps/user ローカル
 * (postFieldsHelpers.ts) なので、その小さな絞り込みロジックを本ファイル内に複製する。
 *
 * 設計原則:
 *   - 値は `Record<string, unknown>` で flat 管理 (Post.extraFields と同形)。
 *   - Firestore への書き込みは一切行わない。純粋な controlled inputs で `onChange(key, value)`
 *     を呼ぶだけ (親ページが state を保持し、最終的に bulkCreatePhotoPosts へ渡す)。
 *   - displayOrder 昇順 + mode='hidden' / visible===false / 組込 builtin (photo/title/comment/
 *     location/detail) / type='photo'|'location' を除外。
 *   - 表示スタイル (docs/105 §① FieldDisplayStyle) を `resolveFieldDisplayStyle` で解決し、
 *     PostExtraFieldsForm と同じ見た目に揃える。
 *   - Tailwind only、inline style 禁止 (CLAUDE.md デザインシステム)。
 *   - 選択肢「その他」に自由記述欄を付けた項目は、「その他」を選んだとき項目の下に記述欄を出す
 *     (記述内容は `{key}__other`、`fieldOtherText.ts`)。
 *
 * 対応する field type (8 種):
 *   text / textarea / number / select / multiselect / checkbox / rating-scale / date。
 *   photo / location は本コンポーネントでは扱わない (絞り込みで除外、地図ピッカー等は親側 UI)。
 */

/**
 * PostForm 本体 (apps/user) が専用 UI で直接 handle する組込 field key。
 * 写真一括投稿では title/comment/location/photo は写真カード側で扱い、detail も別枠のため、
 * extraFields renderer では重複描画を避けて skip する。
 * (apps/user の `EXCLUDED_POST_FIELD_KEYS` を逐語複製。category は除外しない = 汎用描画に委ねる。)
 */
const EXCLUDED_POST_FIELD_KEYS: ReadonlySet<string> = new Set([
  'title',
  'comment',
  'location',
  'photo',
  'detail',
]);

/**
 * 描画対象とする postFields を絞り込む (apps/user `getRenderablePostFields` の複製 + visible 条件)。
 *   1. `mode === 'hidden'` を skip
 *   2. `visible === false` を skip (詳細非表示の項目は入力もさせない)
 *   3. `EXCLUDED_POST_FIELD_KEYS` (本体専用 builtin) を skip
 *   4. `type === 'photo' / 'location'` を skip (専用 UI と重複)
 *   5. `displayOrder` 昇順 sort、未指定は末尾扱い
 */
function getRenderablePostFields(fields: FieldDef[]): FieldDef[] {
  return fields
    .filter((f) => f.mode !== 'hidden')
    .filter((f) => f.visible !== false)
    .filter((f) => !EXCLUDED_POST_FIELD_KEYS.has(f.key))
    .filter((f) => f.type !== 'photo' && f.type !== 'location')
    .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
}

export interface PhotoPostFieldsFormProps {
  /**
   * district.dataSettings.postFields。地区独自の追加入力項目の定義一覧。
   * (親側で `resolvePostFields` 相当の解決済みを渡す想定。)
   */
  postFields: FieldDef[];
  /** 1 枚の写真に対する現在の入力値辞書 (key === FieldDef.key)、初期は `{}`。 */
  values: Record<string, unknown>;
  /** 1 項目の値が変わったときに呼ぶ。親が `{ ...values, [key]: value }` で state 更新する。 */
  onChange: (key: string, value: unknown) => void;
  /** 保存中などの一括 disable。 */
  disabled?: boolean;
}

/**
 * postFields 駆動の編集可能フォーム。各 RENDERABLE postField を型に応じた input として描画する。
 * 値の永続化は行わず、`onChange(key, value)` 経由で親に通知するのみ。
 */
export function PhotoPostFieldsForm({
  postFields,
  values,
  onChange,
  disabled,
}: PhotoPostFieldsFormProps) {
  const ordered = getRenderablePostFields(postFields);
  if (ordered.length === 0) return null;

  /** 1 項目の入力欄 (型 × 表示スタイル)。 */
  const renderField = (field: FieldDef) => {
    const required = field.mode === 'required';
    const value = values[field.key];
    // 表示スタイルを解決 (top-level displayStyle → 旧 optionsMeta → 型既定)。
    const style = resolveFieldDisplayStyle(field) || getDefaultDisplayStyle(field.type);
    // options は string[] 等の旧データも安全に正規化 (select/multiselect で使用)。
    const options: FieldOption[] = normalizeFieldOptions(field.options);

    const labelNode = (
      <span className="block text-xs font-medium text-slate-600 mb-1">
        {field.label} {required && <span className="text-danger-600">*</span>}
      </span>
    );
    const description = field.description ? (
      <p className="text-[11px] text-slate-500 mb-1">{field.description}</p>
    ) : null;

    // --- text / textarea ---
    if (field.type === 'text' || field.type === 'textarea') {
      const max = resolveFieldMaxLength(field);
      const strVal = typeof value === 'string' ? value : '';
      const counter =
        max !== null ? (
          <small className="mt-0.5 block text-[10px] text-slate-400">
            {strVal.length} / {max}
          </small>
        ) : null;
      if (style === 'text-input') {
        return (
          <label key={field.key} className="block">
            {labelNode}
            {description}
            <input
              type="text"
              value={strVal}
              onChange={(e) => onChange(field.key, e.target.value)}
              disabled={disabled}
              maxLength={max ?? undefined}
              className="block w-full rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
            />
            {counter}
          </label>
        );
      }
      return (
        <label key={field.key} className="block">
          {labelNode}
          {description}
          <textarea
            value={strVal}
            onChange={(e) => onChange(field.key, e.target.value)}
            disabled={disabled}
            maxLength={max ?? undefined}
            rows={3}
            className="block w-full rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
          />
          {counter}
        </label>
      );
    }

    // --- number ---
    if (field.type === 'number') {
      const num = typeof value === 'number' ? value : null;
      if (style === 'slider') {
        const min = field.optionsMeta?.min ?? 0;
        const max = field.optionsMeta?.max ?? 100;
        const current = num ?? min;
        return (
          <label key={field.key} className="block">
            {labelNode}
            {description}
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={min}
                max={max}
                value={current}
                onChange={(e) => onChange(field.key, Number(e.target.value))}
                disabled={disabled}
                className="flex-1 disabled:opacity-50"
              />
              <span className="min-w-[3rem] text-right font-mono text-sm">{current}</span>
            </div>
          </label>
        );
      }
      if (style === 'stepper') {
        const min = field.optionsMeta?.min;
        const max = field.optionsMeta?.max;
        const cur = num ?? 0;
        const clamp = (v: number) => {
          if (typeof min === 'number' && v < min) return min;
          if (typeof max === 'number' && v > max) return max;
          return v;
        };
        return (
          <div key={field.key}>
            {labelNode}
            {description}
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="減らす"
                onClick={() => onChange(field.key, clamp(cur - 1))}
                disabled={disabled}
                className="h-9 w-9 rounded border border-slate-300 text-lg disabled:opacity-50"
              >
                −
              </button>
              <input
                type="number"
                value={num === null ? '' : String(num)}
                onChange={(e) =>
                  onChange(field.key, e.target.value === '' ? null : Number(e.target.value))
                }
                disabled={disabled}
                className="w-20 rounded border border-slate-300 px-2 py-1.5 text-center text-sm disabled:bg-slate-100"
              />
              <button
                type="button"
                aria-label="増やす"
                onClick={() => onChange(field.key, clamp(cur + 1))}
                disabled={disabled}
                className="h-9 w-9 rounded border border-slate-300 text-lg disabled:opacity-50"
              >
                ＋
              </button>
            </div>
          </div>
        );
      }
      return (
        <label key={field.key} className="block">
          {labelNode}
          {description}
          <input
            type="number"
            value={num === null ? '' : String(num)}
            onChange={(e) =>
              onChange(field.key, e.target.value === '' ? null : Number(e.target.value))
            }
            disabled={disabled}
            className="block w-full rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
          />
        </label>
      );
    }

    // --- select (単一選択) ---
    if (field.type === 'select') {
      const strVal = typeof value === 'string' ? value : '';
      if (style === 'buttons') {
        return (
          <div key={field.key}>
            {labelNode}
            {description}
            <div role="radiogroup" className="flex flex-wrap items-center gap-2">
              {options.map((opt) => {
                const active = strVal === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={disabled}
                    onClick={() => pick(field, active ? '' : opt.value)}
                    className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                      active
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }
      if (style === 'radio' || style === 'checkboxes') {
        return (
          <div key={field.key}>
            {labelNode}
            {description}
            <div className="grid grid-cols-1 gap-1 rounded-md border border-slate-200 p-3">
              {options.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 py-0.5">
                  <input
                    type="radio"
                    name={`pf-${field.key}`}
                    checked={strVal === opt.value}
                    onChange={() => pick(field, opt.value)}
                    disabled={disabled}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      }
      // default: dropdown
      return (
        <label key={field.key} className="block">
          {labelNode}
          {description}
          <select
            value={strVal}
            onChange={(e) => pick(field, e.target.value)}
            disabled={disabled}
            className="block w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm disabled:bg-slate-100"
          >
            <option value="">未選択</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      );
    }

    // --- multiselect (複数選択) ---
    if (field.type === 'multiselect') {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      if (style === 'buttons') {
        return (
          <div key={field.key}>
            {labelNode}
            {description}
            <div className="flex flex-wrap items-center gap-2">
              {options.map((opt) => {
                const active = arr.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={active}
                    disabled={disabled}
                    onClick={() =>
                      pick(field, active ? arr.filter((x) => x !== opt.value) : [...arr, opt.value])
                    }
                    className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                      active
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }
      if (style === 'dropdown') {
        return (
          <label key={field.key} className="block">
            {labelNode}
            {description}
            <select
              multiple
              value={arr}
              onChange={(e) =>
                pick(
                  field,
                  Array.from(e.target.selectedOptions).map((o) => o.value),
                )
              }
              disabled={disabled}
              size={Math.min(6, Math.max(2, options.length))}
              className="block w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm disabled:bg-slate-100"
            >
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        );
      }
      // default: checkboxes
      return (
        <div key={field.key}>
          {labelNode}
          {description}
          <div className="grid grid-cols-1 gap-1 rounded-md border border-slate-200 p-3">
            {options.map((opt) => {
              const checked = arr.includes(opt.value);
              return (
                <label key={opt.value} className="flex items-center gap-2 py-0.5">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      pick(
                        field,
                        e.target.checked ? [...arr, opt.value] : arr.filter((x) => x !== opt.value),
                      )
                    }
                    disabled={disabled}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    }

    // --- checkbox (boolean) ---
    if (field.type === 'checkbox') {
      const on = Boolean(value);
      if (style === 'toggle') {
        return (
          <div key={field.key}>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-label={field.label}
              disabled={disabled}
              onClick={() => onChange(field.key, !on)}
              className={`flex items-center gap-2 rounded-full border px-3 py-1.5 transition disabled:opacity-50 ${
                on
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-slate-300 bg-white text-slate-700'
              }`}
            >
              <span
                className={`relative inline-block h-5 w-9 rounded-full ${
                  on ? 'bg-white/30' : 'bg-slate-200'
                }`}
              >
                <span
                  className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                    on ? 'right-0.5' : 'left-0.5'
                  }`}
                />
              </span>
              <span className="text-sm">
                {field.label} {required && <span className="text-danger-600">*</span>}
              </span>
            </button>
            {description}
          </div>
        );
      }
      return (
        <div key={field.key}>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={on}
              onChange={(e) => onChange(field.key, e.target.checked)}
              disabled={disabled}
              className="h-4 w-4"
            />
            <span className="text-sm">
              {field.label} {required && <span className="text-danger-600">*</span>}
            </span>
          </label>
          {description}
        </div>
      );
    }

    // --- rating-scale ---
    if (field.type === 'rating-scale') {
      const min = field.optionsMeta?.min ?? -3;
      const max = field.optionsMeta?.max ?? 3;
      const excludeZero = field.optionsMeta?.excludeZero === true;
      const posLabel = field.optionsMeta?.scaleLabels?.posLabel;
      const negLabel = field.optionsMeta?.scaleLabels?.negLabel;
      const steps: number[] = [];
      for (let n = min; n <= max; n++) {
        if (excludeZero && n === 0) continue;
        steps.push(n);
      }
      const selected = typeof value === 'number' ? value : undefined;
      const scaleLabelFooter =
        negLabel || posLabel ? (
          <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
            <span>{negLabel ?? ''}</span>
            <span>{posLabel ?? ''}</span>
          </div>
        ) : null;

      if (style === 'slider' && steps.length > 0) {
        const sMin = steps[0] as number;
        const sMax = steps[steps.length - 1] as number;
        const current = selected ?? sMin;
        return (
          <div key={field.key}>
            {labelNode}
            {description}
            <div className="flex items-center gap-3">
              {negLabel && (
                <span className="shrink-0 text-[11px] text-slate-500">← {negLabel}</span>
              )}
              <input
                type="range"
                min={sMin}
                max={sMax}
                step={1}
                value={current}
                onChange={(e) => onChange(field.key, Number(e.target.value))}
                disabled={disabled}
                className="flex-1 disabled:opacity-50"
              />
              {posLabel && (
                <span className="shrink-0 text-[11px] text-slate-500">{posLabel} →</span>
              )}
            </div>
            <div className="mt-1 text-center font-mono text-sm">
              {selected === undefined ? '（未選択）' : current > 0 ? `+${current}` : current}
            </div>
          </div>
        );
      }

      if (style === 'stars') {
        const positives = steps.filter((v) => v > 0);
        if (positives.length > 0) {
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              <div className="flex items-center gap-1">
                {positives.map((v) => {
                  const active = selected !== undefined && selected >= v;
                  return (
                    <button
                      key={v}
                      type="button"
                      aria-label={`${v} つ星`}
                      aria-pressed={active}
                      disabled={disabled}
                      onClick={() => onChange(field.key, selected === v ? null : v)}
                      className={`text-2xl disabled:opacity-50 ${
                        active ? 'text-amber-500' : 'text-slate-300'
                      }`}
                    >
                      ★
                    </button>
                  );
                })}
                <span className="ml-2 text-sm text-slate-500">{selected ?? '－'}</span>
              </div>
              {scaleLabelFooter}
            </div>
          );
        }
        // 負値を含む scale は stars が成立しない → 既定 buttons へ fallback。
      }

      // default: buttons
      return (
        <div key={field.key}>
          {labelNode}
          {description}
          <div role="radiogroup" className="flex flex-wrap items-center gap-2">
            {steps.map((n) => {
              const active = selected === n;
              return (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={disabled}
                  onClick={() => onChange(field.key, n)}
                  className={`min-w-[2.5rem] rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                    active
                      ? 'border-primary-600 bg-primary-600 text-white'
                      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {n > 0 ? `+${n}` : n}
                </button>
              );
            })}
          </div>
          {scaleLabelFooter}
        </div>
      );
    }

    // --- date ---
    if (field.type === 'date') {
      return (
        <label key={field.key} className="block">
          {labelNode}
          {description}
          <input
            type="date"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(field.key, e.target.value)}
            disabled={disabled}
            className="block w-full rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
          />
        </label>
      );
    }

    // photo / location は絞り込みで除外済み (到達しない)。未知型も安全に skip。
    return null;
  };

  /**
   * select / multiselect の選択値を変える。「その他」を外したら記述内容を '' にする
   * (保存側がマップ全体を書くので key を落とさず空にする)。
   */
  const pick = (field: FieldDef, next: string | string[]) => {
    onChange(field.key, next);
    const other = findOtherOption({ ...field, options: normalizeFieldOptions(field.options) });
    const k = otherTextKey(field.key);
    if (other && !isOtherChosen(other.value, next) && values[k]) onChange(k, '');
  };

  /** 「その他」を選んでいるときだけ、その項目の下に自由記述欄を出す。 */
  const renderOtherText = (field: FieldDef) => {
    const other = findOtherOption({ ...field, options: normalizeFieldOptions(field.options) });
    if (!other || !isOtherChosen(other.value, values[field.key])) return null;
    const k = otherTextKey(field.key);
    const v = values[k];
    return (
      <OtherTextInput
        value={typeof v === 'string' ? v : ''}
        onChange={(t) => onChange(k, t)}
        ariaLabel={`${field.label}（${other.label}の内容）`}
        disabled={disabled}
      />
    );
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      {ordered.map((field) => (
        <Fragment key={field.key}>
          {renderField(field)}
          {renderOtherText(field)}
        </Fragment>
      ))}
    </div>
  );
}
